/**
 * NotificationBell - Unread count badge + link to notifications page.
 *
 * Polls for unread count periodically and displays a badge when > 0.
 *
 * Requirements: 27.5
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

const POLL_INTERVAL_MS = 60_000; // Poll every 60 seconds

export function NotificationBell() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    try {
      const response = await apiClient.get<{ count: number }>('/notifications/unread-count');
      setUnreadCount(response.data.count ?? response.data.unreadCount ?? 0);
    } catch {
      // Silently fail — don't disrupt UX for notification count
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchUnreadCount();

    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  return (
    <Link
      to="/notifications"
      className="relative min-h-[44px] min-w-[44px] rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      aria-label={
        unreadCount > 0
          ? t('notifications.unreadCount', { count: unreadCount })
          : t('nav.notifications')
      }
    >
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>

      {/* Unread count badge */}
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-error-500 px-1 text-[10px] font-bold text-white">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </Link>
  );
}
